import { TUseTableProps, useTable } from "@/hooks/useTable";
import { MRT_RowData, MRT_RowSelectionState } from "material-react-table";
import { Key, useMemo, useState } from "react";

type TUseSelectionTableProps<T extends MRT_RowData> = {
  primaryKey: keyof T;
  defaultSelectionKeys?: Key[];
} & TUseTableProps<T>;

export const useSelectionTable = <T extends MRT_RowData>({ primaryKey, defaultSelectionKeys, ...rest }: TUseSelectionTableProps<T>) => {
  const formatSelectionKeys = (data: Key[]) => {
    return data.reduce((cur, item) => ({ ...cur, [item.toString()]: true }), {} as MRT_RowSelectionState);
  };

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(formatSelectionKeys(defaultSelectionKeys ?? []));

  const clearSelection = () => {
    setRowSelection({});
  };

  const removeRowSelectionById = (id: string | number) => {
    setRowSelection((cur) => {
      if (!cur[id]) return cur;
      const { [id]: _, ...rest } = cur;
      return rest;
    });
  };

  const rowSelectionKeys = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  const { table } = useTable({
    enableRowSelection: true,
    getRowId: (row, index) => {
      // getRowId by primaryKey of the table
      if (primaryKey in row) return JSON.stringify(row[primaryKey]);
      return index.toString();
    },
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    ...rest,
  });

  return { table, rowSelectionKeys, clearSelection, removeRowSelectionById };
};
